import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  EMPTY_JOURNEY, applyJourneyOutcome, createGame, dailyAnswer, isSupportedLength,
  journeyCompletedBy, loadLength, type GameState, type JourneyState, type Language,
} from '@lexicon/core';
import { JourneyProgress } from '../components/JourneyProgress';
import { HexagonIcon } from '../components/icons';
import { loadJourney } from '../game/journeyStore';
import { pickJourneyWord } from '../game/journeyWord';
import {
  JOURNEY_SESSION_ID, clearSession, loadSession, saveSession, toGameState, toSession,
  type GameResult,
} from '../game/session';
import { useLengthValidator } from '../game/useLengthValidator';
import { useApp, useStrings } from '../state/AppContext';
import { GamePlay } from './GamePlay';

interface Boot {
  /** Bumped for every new game, so GamePlay remounts on a fresh word. */
  seq: number;
  /** The language this run belongs to; a switch invalidates the boot. */
  language: Language;
  /** Journey state as it stood when this game started. */
  journey: JourneyState;
  game: GameState;
  result: GameResult | null;
  revealedInsights: readonly string[];
}

function JourneyBanner({ rung, journey, action }: {
  rung: number; journey: JourneyState; action: ReactNode;
}) {
  const t = useStrings();
  return (
    <section className="glass overflow-hidden rounded-2xl" aria-label={t.journey.sectionLabel}>
      <div className="flex items-center gap-3 px-4 py-2 sm:py-3">
        <HexagonIcon className="h-4 w-4 shrink-0 text-accent" />
        <span className="shrink-0 whitespace-nowrap text-xs font-semibold tracking-[0.18em]">
          {t.journey.label}
        </span>
        <div className="min-w-0 flex-1">
          <JourneyProgress
            rung={rung}
            bestRung={journey.bestRung}
            journeysCompleted={journey.journeysCompleted}
          />
        </div>
        {action ? <div className="shrink-0 whitespace-nowrap">{action}</div> : null}
      </div>
    </section>
  );
}

export function JourneyScreen() {
  const { ready, storage, today, language, t } = useApp();
  const [seq, setSeq] = useState(0);
  const [boot, setBoot] = useState<Boot | null>(null);
  /** The live outcome, once this visit's game finishes. */
  const [outcome, setOutcome] = useState<boolean | null>(null);
  /** Set when "Next word" could not clear the finished game from storage. */
  const [nextError, setNextError] = useState(false);

  useEffect(() => {
    // Cleared synchronously so the next render can never pair one game's
    // board with another game's rung — the same discipline DailyScreen
    // uses across a day rollover.
    setBoot(null);
    setOutcome(null);
    setNextError(false);
    if (!ready) return undefined;
    let cancelled = false;
    const bootSeq = seq;

    void (async () => {
      let journey: JourneyState = { ...EMPTY_JOURNEY };
      let game: GameState | null = null;
      let result: GameResult | null = null;
      let revealedInsights: readonly string[] = [];
      try {
        journey = await loadJourney(storage, language);
        const session = await loadSession(storage, 'journey', JOURNEY_SESSION_ID, language);
        // A stored answer already passed isSession's 4–7 shape check; the
        // length guard is belt-and-braces before maxGuessesFor sees it.
        if (session && isSupportedLength(session.answer.length)) {
          game = toGameState(session);
          result = session.result;
          revealedInsights = session.revealedInsights ?? [];
        }
      } catch {
        // Spec §7: a broken read starts a fresh run rather than wedging
        // the screen on "Loading…".
      }
      if (cancelled) return;

      if (!game) {
        // Never today's daily answer (spec §10.3).
        game = createGame(
          pickJourneyWord(journey.rung, dailyAnswer(today, language), Math.random, language),
          'journey',
        );
        // Checked again immediately before the write: under the native
        // (Preferences bridge) storage adapter, `saveSession` is a real
        // async round-trip, and a cancelled effect must not still hand a
        // freshly-picked word to storage a later effect run might already
        // be racing (write-ordering hazard).
        if (cancelled) return;
        // Persisted before the board renders: a reload one keystroke later
        // must resume THIS word, not roll a new one.
        try {
          await saveSession(storage, toSession(JOURNEY_SESSION_ID, game, null), language);
        } catch { /* unplayable storage still gets a playable game */ }
        if (cancelled) return;
      }

      // Warmed here, ahead of the board's first paint, so the very first
      // Enter press on a non-bundled rung (4/6/7) does not race the chunk
      // load and land on "Checking word list…" (spec §10.2/§10.5) — the
      // same guarantee ChallengePlayScreen gives its own boot. This is a
      // head start, not a requirement: `useLengthValidator` below still
      // owns `isValid` and retries on its own if this one failed.
      try {
        await loadLength(game.answer.length, language);
      } catch { /* useLengthValidator's own retry path covers this */ }
      if (cancelled) return;

      setBoot({ seq: bootSeq, language, journey, game, result, revealedInsights });
    })();

    return () => { cancelled = true; };
  }, [ready, storage, today, seq, language]);

  const startNext = useCallback(() => {
    void (async () => {
      // Drop the finished game before re-booting: boot restores whatever
      // is stored, and only rolls a fresh word when nothing loads — so
      // with this session still present it would just re-show this game's
      // result sheet instead of dealing a new word. No later write papers
      // over a failed clear. (The journey *state* was already advanced by
      // finalize and is re-read on the way back through.)
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          await clearSession(storage, 'journey', JOURNEY_SESSION_ID, language);
          setNextError(false);
          setSeq((n) => n + 1);
          return;
        } catch { /* retry once — bridge failures are usually transient */ }
      }
      // Still failing: stay on this result sheet and say why, rather than
      // silently re-booting into the same finished game.
      setNextError(true);
    })();
  }, [storage, language]);

  const handleFinalized = useCallback((r: GameResult) => setOutcome(r.won), []);

  // Hook order is fixed, so this runs on every render — `null` while
  // booting keeps it from fetching a chunk for a rung we may not play.
  const rung = boot ? boot.game.answer.length : null;
  const { isValid, retry } = useLengthValidator(rung, language);

  if (!boot || boot.seq !== seq || boot.language !== language) {
    return <p className="p-6 text-center text-dim">{t.journey.loading}</p>;
  }

  const before = boot.journey;
  // `outcome` normally arrives from onFinalized; `boot.result` covers the
  // first render after a reload onto an already-finished game.
  const won = outcome ?? boot.result?.won ?? null;
  // A restored, already-finished game was finalized on an earlier visit —
  // finalizeGame already advanced the journey THEN, and `boot.journey`
  // (loaded in that same boot pass) already IS the post-outcome state.
  // Re-running it through `applyJourneyOutcome`/`journeyCompletedBy` here
  // would climb the rung a second time (or, for the rung-7 case, miss the
  // completion banner entirely since `before` has already wrapped back to
  // 4). So a restored result is read straight off itself instead of
  // replayed through the transition; only a *live* finish (`outcome`, set
  // by onFinalized during this visit) still derives `after`/`completed`
  // from `before`.
  const restored = boot.result !== null;
  const after = restored || won === null ? before : applyJourneyOutcome(before, won);
  const completed = restored
    ? boot.result?.xpJourneyBonus !== undefined
    : won !== null && journeyCompletedBy(before, won);

  return (
    <GamePlay
      key={`${language}:${boot.seq}:${boot.game.answer}`}
      id={JOURNEY_SESSION_ID}
      initial={boot.game}
      initialResult={boot.result}
      initialRevealedInsights={boot.revealedInsights}
      isValid={isValid}
      onValidationPending={retry}
      onFinalized={handleFinalized}
      shareId={boot.game.answer.length}
      ctaLabel={completed ? t.journey.startNew : t.journey.nextWord(after.rung)}
      onCta={startNext}
      resultBanner={(
        <>
          <JourneyProgress
            rung={after.rung}
            bestRung={after.bestRung}
            journeysCompleted={after.journeysCompleted}
            complete={completed}
          />
          {nextError ? (
            <p role="alert" className="mt-2 text-sm text-danger">{t.journey.nextFailed}</p>
          ) : null}
        </>
      )}
      banner={({ finished, openResult }) => (
        <JourneyBanner
          rung={boot.game.answer.length}
          journey={before}
          action={finished ? (
            <button
              type="button"
              className="min-h-[44px] whitespace-nowrap text-sm text-dim"
              onClick={openResult}
            >
              {t.common.viewResult}
            </button>
          ) : null}
        />
      )}
    />
  );
}
